import React from 'react'
import classNames from 'classnames'
import FileDownloadIcon from '@rsuite/icons/FileDownload'
import ArrowRightLineIcon from '@rsuite/icons/ArrowRightLine'
import { DocumentSummary } from '../types'
import {
  displayFileName,
  formatAmount,
  formatDateTime,
  formatDocumentDate,
  parseSender
} from '../utils'
import StatusBadge from './StatusBadge'

type Props = {
  rows: DocumentSummary[]
  loading: boolean
  onOpen: (doc: DocumentSummary) => void
  onDownload: (doc: DocumentSummary) => void
  empty: React.ReactNode
}

const SKELETON_ROWS = 8

const DocumentsTable: React.FC<Props> = ({
  rows,
  loading,
  onOpen,
  onDownload,
  empty
}) => {
  const onKey = (e: React.KeyboardEvent, doc: DocumentSummary) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onOpen(doc)
    }
  }

  return (
    <div className="dx-table-wrap">
      <table className="dx-table">
        <thead>
          <tr>
            <th scope="col">Status</th>
            <th scope="col">Received</th>
            <th scope="col">Counterparty</th>
            <th scope="col">Reference</th>
            <th scope="col">Document date</th>
            <th scope="col" className="dx-num">
              Total
            </th>
            <th scope="col" className="dx-num">
              Lines
            </th>
            <th scope="col" className="dx-table__actions">
              <span className="dx-sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {loading &&
            Array.from({ length: SKELETON_ROWS }, (_, i) => (
              <tr
                key={`skeleton-${i}`}
                className="dx-table__skeleton"
                aria-hidden
              >
                {Array.from({ length: 8 }, (__, j) => (
                  <td key={j}>
                    <span />
                  </td>
                ))}
              </tr>
            ))}

          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={8} className="dx-table__empty">
                {empty}
              </td>
            </tr>
          )}

          {!loading &&
            rows.map((doc) => {
              const sender = parseSender(doc.fromEmail)
              const party = doc.supplierName || doc.payerName
              return (
                <tr
                  key={doc.id}
                  tabIndex={0}
                  className={classNames('dx-table__row', {
                    'dx-table__row--attention':
                      doc.queue === 'NEEDS_REVIEW' || doc.queue === 'FAILED'
                  })}
                  onClick={() => onOpen(doc)}
                  onKeyDown={(e) => onKey(e, doc)}
                  aria-label={`Open ${party ?? displayFileName(doc)}`}
                >
                  <td>
                    <StatusBadge doc={doc} />
                  </td>
                  <td className="dx-nowrap">
                    {formatDateTime(doc.receivedAt)}
                    <span className="dx-sub">
                      {doc.source === 'UPLOAD'
                        ? 'Uploaded'
                        : sender.name || sender.email || 'Email'}
                    </span>
                  </td>
                  <td className="dx-table__party">
                    <span
                      className={classNames('dx-strong', {
                        'dx-muted': !party
                      })}
                      title={party}
                    >
                      {party ||
                        (doc.extracted
                          ? 'Counterparty not found'
                          : 'Not read yet')}
                    </span>
                    <span className="dx-sub dx-mono" title={doc.fileName}>
                      {displayFileName(doc)}
                    </span>
                  </td>
                  <td className="dx-mono">{doc.reference || '—'}</td>
                  <td className="dx-nowrap">
                    {formatDocumentDate(doc.documentDate)}
                  </td>
                  <td className="dx-num dx-nowrap">
                    {formatAmount(doc.totalAmount, doc.currency)}
                  </td>
                  <td className="dx-num">{doc.lineItemCount ?? '—'}</td>
                  <td className="dx-table__actions">
                    <div className="dx-row-actions">
                      <button
                        type="button"
                        className="dx-icon-btn"
                        title="Download PDF"
                        aria-label="Download PDF"
                        onClick={(e) => {
                          e.stopPropagation()
                          onDownload(doc)
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <FileDownloadIcon />
                      </button>
                      <span className="dx-row-actions__open" aria-hidden>
                        <ArrowRightLineIcon />
                      </span>
                    </div>
                  </td>
                </tr>
              )
            })}
        </tbody>
      </table>
    </div>
  )
}

export default DocumentsTable
